import {
  eventFeed,
  EventFeedFilterGroupKeys,
  EventFeedFiltersSchemas,
} from '@api';
import { SharedFilterState } from '@terminal/hooks/use-filter-actions/useFilterActions';
import {
  INSIGHT_FEED_INCLUDE_COMPETITORS,
  INSIGHT_FEED_SELECTED_WATCHLISTS,
  useRemoteStorage,
} from '@terminal/hooks/use-remote-storage';
import { apiFetch } from '@terminal/utils/api-fetch/apiFetch';
import { mapEventFeedFiltersSchemasToFilterGroups } from '@terminal/utils/event-feed-filters/mapEventFeedSchemasToScreenerFilters';
import { create } from '@terminal/utils/store/createStore';
import {
  InsightsPill,
  MarketCatalystSubTab,
} from '@terminal/views/insight-feed/components/following/following-utils';
import { InsightFeedFilterTabs } from '@terminal/views/insight-feed/components/insight-feed-type-tab-line/InsightFeedTypeTabLine';

import {
  defaultEventFeedFilterGroups,
  tabToEventFeedFilterGroup,
  tabTypeToEventFeedType,
} from './insight-feed-filters-shared';

export { tabToEventFeedFilterGroup, tabTypeToEventFeedType };

interface InsightFeedFiltersStore
  extends SharedFilterState<EventFeedFilterGroupKeys> {
  selectedWatchlistIds: Set<string>;
  isSecondRelationshipEnabled: boolean;
  selectedWatchlistSubTab: InsightFeedFilterTabs;
  selectedPill: InsightsPill;
  selectedMarketCatalystSubTab: MarketCatalystSubTab;
  isInitialized: boolean;
  initializeFromStorage: () => void;
  initEventFeedFilters: () => Promise<void>;
  setSelectedWatchlistIds: (ids: Set<string>) => void;
  setIsSecondRelationshipEnabled: (enabled: boolean) => void;
  handleToggleSecondRelationship: () => void;
  setSelectedWatchlistSubTab: (type: InsightFeedFilterTabs) => void;
  setSelectedPill: (pill: InsightsPill) => void;
  setSelectedMarketCatalystSubTab: (tab: MarketCatalystSubTab) => void;
}

export const useInsightFeedFilters = create<InsightFeedFiltersStore>(set => ({
  selectedWatchlistIds: new Set<string>(),
  isSecondRelationshipEnabled: false,
  selectedWatchlistSubTab: 'all',
  selectedPill: 'all',
  selectedMarketCatalystSubTab: 'all',
  isInitialized: false,
  filterGroups: defaultEventFeedFilterGroups,
  initializeFromStorage: () => {
    const { items } = useRemoteStorage.getState();
    const storedIds = Array.isArray(items?.[INSIGHT_FEED_SELECTED_WATCHLISTS])
      ? items?.[INSIGHT_FEED_SELECTED_WATCHLISTS]
      : undefined;
    const isIncludeCompetitors =
      typeof items?.[INSIGHT_FEED_INCLUDE_COMPETITORS] === 'boolean'
        ? items[INSIGHT_FEED_INCLUDE_COMPETITORS]
        : undefined;

    set({
      ...(storedIds?.length && { selectedWatchlistIds: new Set(storedIds) }),
      ...(isIncludeCompetitors !== undefined && {
        isSecondRelationshipEnabled: isIncludeCompetitors,
      }),
      isInitialized: true,
    });
  },
  initEventFeedFilters: async () => {
    const res = await apiFetch<EventFeedFiltersSchemas>(
      eventFeed.getFilters.path,
      { method: 'get' }
    );

    if (!res.error) {
      set({
        filterGroups: mapEventFeedFiltersSchemasToFilterGroups(
          res.data,
          defaultEventFeedFilterGroups
        ),
      });
    }
  },
  setSelectedWatchlistIds: ids => {
    set({ selectedWatchlistIds: ids });

    const { storeItems } = useRemoteStorage.getState();
    storeItems({ [INSIGHT_FEED_SELECTED_WATCHLISTS]: Array.from(ids) });
  },
  setIsSecondRelationshipEnabled: enabled => {
    set({ isSecondRelationshipEnabled: enabled });

    const { storeItems } = useRemoteStorage.getState();
    storeItems({ [INSIGHT_FEED_INCLUDE_COMPETITORS]: enabled });
  },
  handleToggleSecondRelationship: () => {
    const next = !useInsightFeedFilters.getState().isSecondRelationshipEnabled;
    set({ isSecondRelationshipEnabled: next });

    const { storeItems } = useRemoteStorage.getState();
    storeItems({ [INSIGHT_FEED_INCLUDE_COMPETITORS]: next });
  },
  setSelectedWatchlistSubTab: type => {
    set({ selectedWatchlistSubTab: type });
  },
  setSelectedPill: pill => {
    set({ selectedPill: pill });
  },
  setSelectedMarketCatalystSubTab: tab => {
    set({ selectedMarketCatalystSubTab: tab });
  },
}));
